<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
    import Button from '$lib/components/base/button/button.component.svelte';
    import ChevronRightCircleFilled from '$lib/components/foundation/icons/ChevronRightCircleFilled.component.svelte';
    import ChevronLeftIcon from '$lib/components/foundation/icons/ChevronLeftIcon.component.svelte';
    import ExternalLinkIcon from '$lib/components/foundation/icons/ExternalLinkIcon.component.svelte';
    import InfoIcon from '$lib/components/foundation/icons/InfoIcon.component.svelte';
    import PlusIcon from '$lib/components/foundation/icons/PlusIcon.component.svelte';
    import CancelIcon from '$lib/components/foundation/icons/CancelIcon.component.svelte';

    interface ImageHotspot {
        x: number; // Floating point between 0 and 1
        y: number; // Floating point between 0 and 1
        id: string;
        title: string;
        order: number;
        text: string;
        link: string;
        linkText: string;
    }

    export let imageSrc: string;
    export let imageAlt: string;
    export let hotspots: ImageHotspot[] = [];
    if (typeof hotspots === 'string') {
        try {
            console.log(hotspots);
            hotspots = JSON.parse(hotspots);
        } catch (e) {
            console.error('Failed to parse hotspots JSON:', e);
            hotspots = [];
        }
    }

    export let isProcess: boolean = false;
    if (typeof isProcess === 'string') {
        isProcess = isProcess === 'true' || isProcess === '1';
    }
    export let hotspotTheme: null | 'green' | 'darkgray' = null;
    export let iconName: string | null = null;

    let openHotspotId: string | null = null;
    let imageContainer: HTMLDivElement;
    let popoverElement: HTMLDivElement;
    let cleanupAutoUpdate: (() => void) | null = null;
    let currentTriggerElement: HTMLElement | null = null;
    let closeButtonElement: HTMLButtonElement;
    let liveRegion: HTMLDivElement;
    let hasOpenedHotspot = false;

    // Sort hotspots by order for process navigation
    $: sortedHotspots = [...hotspots].sort((a, b) => a.order - b.order);
    $: openHotspot = openHotspotId ? hotspots.find((h) => h.id === openHotspotId) : null;
    $: currentIndex = openHotspot ? sortedHotspots.findIndex((h) => h.id === openHotspotId) : -1;
    $: canGoPrevious = isProcess && currentIndex > 0;
    $: canGoNext = isProcess && currentIndex < sortedHotspots.length - 1;

    let hotspotThemeClasses = '';

    switch (hotspotTheme) {
        case 'green':
            hotspotThemeClasses =
                'bg-prevent-primary-green text-white hover:bg-prevent-primary-green-dark';
            break;
        case 'darkgray':
            hotspotThemeClasses = 'bg-prevent-primary-text text-white hover:bg-gray-900';
            break;
        default:
            hotspotThemeClasses = 'bg-white hover:bg-gray-100 ';
    }

    function toggleHotspot(hotspotId: string, referenceElement: HTMLElement, event: MouseEvent) {
        event.stopPropagation();

        if (!hasOpenedHotspot) {
            hasOpenedHotspot = true;
        }

        if (openHotspotId === hotspotId) {
            closeHotspot();
        } else {
            currentTriggerElement = referenceElement;
            openHotspotId = hotspotId;
            updatePopoverPosition(referenceElement);
            // Focus the close button when popover opens
            setTimeout(() => {
                if (closeButtonElement) {
                    closeButtonElement.focus();
                }
            }, 0);
        }
    }

    function closeHotspot() {
        if (cleanupAutoUpdate) {
            cleanupAutoUpdate();
            cleanupAutoUpdate = null;
        }
        openHotspotId = null;
        // Return focus to the trigger button
        if (currentTriggerElement) {
            currentTriggerElement.focus();
            currentTriggerElement = null;
        }
    }

    function goToPrevious(event?: MouseEvent) {
        if (event) {
            event.stopPropagation();
        }
        if (canGoPrevious) {
            const prevHotspot = sortedHotspots[currentIndex - 1];
            const hotspotElement = imageContainer.querySelector(
                `[data-hotspot-id="${prevHotspot.id}"]`
            ) as HTMLElement;
            if (hotspotElement) {
                currentTriggerElement = hotspotElement;
                openHotspotId = prevHotspot.id;
                // Wait for DOM to update before positioning
                setTimeout(() => {
                    updatePopoverPosition(hotspotElement);
                }, 0);
                // Announce change to screen readers
                if (liveRegion) {
                    liveRegion.textContent = `Visar bildpunkt ${prevHotspot.order}: ${prevHotspot.title}`;
                }
            }
        }
    }

    function goToNext(event?: MouseEvent) {
        if (event) {
            event.stopPropagation();
        }
        if (canGoNext) {
            const nextHotspot = sortedHotspots[currentIndex + 1];
            const hotspotElement = imageContainer.querySelector(
                `[data-hotspot-id="${nextHotspot.id}"]`
            ) as HTMLElement;
            if (hotspotElement) {
                currentTriggerElement = hotspotElement;
                openHotspotId = nextHotspot.id;
                // Wait for DOM to update before positioning
                setTimeout(() => {
                    updatePopoverPosition(hotspotElement);
                }, 0);
                // Announce change to screen readers
                if (liveRegion) {
                    liveRegion.textContent = `Visar bildpunkt ${nextHotspot.order}: ${nextHotspot.title}`;
                }
            }
        }
    }

    async function updatePopoverPosition(referenceElement: HTMLElement) {
        if (!popoverElement || !referenceElement) return;

        // Clean up previous autoUpdate if exists
        if (cleanupAutoUpdate) {
            cleanupAutoUpdate();
        }

        // Set up autoUpdate to keep popover positioned
        cleanupAutoUpdate = autoUpdate(referenceElement, popoverElement, async () => {
            const { x, y, placement, middlewareData } = await computePosition(
                referenceElement,
                popoverElement,
                {
                    placement: 'top',
                    middleware: [offset(12), flip(), shift({ padding: 8 })]
                }
            );

            Object.assign(popoverElement.style, {
                left: `${x}px`,
                top: `${y}px`
            });
        });
    }

    function handleClickOutside(event: MouseEvent) {
        if (!openHotspotId) return;

        const target = event.target as HTMLElement;

        // Check if click is on a hotspot button
        const clickedHotspot = target.closest('[data-hotspot-id]');
        if (clickedHotspot) return;

        // Check if click is inside the popover (including checking event path)
        if (popoverElement) {
            // Check if target is inside popover
            if (popoverElement.contains(target)) return;

            // Also check event path/composedPath for shadow DOM and other cases
            const path = event.composedPath?.() || [];
            if (path.includes(popoverElement)) return;
        }

        // Click was outside - close the popover
        closeHotspot();
    }

    function handleKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && openHotspotId) {
            closeHotspot();
        }
    }

    onMount(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleKeydown);
    });

    onDestroy(() => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleKeydown);
        if (cleanupAutoUpdate) {
            cleanupAutoUpdate();
        }
    });
</script>

<!-- Live region for screen reader announcements -->
<div bind:this={liveRegion} class="sr-only" role="status" aria-live="polite" aria-atomic="true">
</div>

<div class="relative inline-block" bind:this={imageContainer}>
    <img src={imageSrc} alt={imageAlt} class="max-w-full h-auto" draggable="false" />

    {#each hotspots as hotspot (hotspot.id)}
        <div
            class="absolute -ml-5 -mt-5"
            style="left: {hotspot.x * 100}%; top: {hotspot.y * 100}%;">
            {#if !hasOpenedHotspot}
                <span
                    class="absolute inline-flex h-full w-full animate-ping rounded-full {hotspotThemeClasses} opacity-75">
                </span>
            {/if}

            <button
                type="button"
                data-hotspot-id={hotspot.id}
                class="relative z-10 w-10 h-10 rounded-full {hotspotThemeClasses} font-semibold flex items-center justify-center cursor-pointer hover:scale-110 transition-all shadow-lg {openHotspotId ===
                hotspot.id
                    ? 'ring-4 ring-prevent-primary-dark-green-100 scale-110'
                    : ''}"
                on:click={(e) => toggleHotspot(hotspot.id, e.currentTarget, e)}
                aria-label="Bildpunkt {hotspot.order}: {hotspot.title}"
                aria-expanded={openHotspotId === hotspot.id}
                aria-controls="hotspot-popover">
                {#if isProcess}
                    {hotspot.order}
                {:else if iconName === null}
                    <InfoIcon class="w-6 h-6" aria-hidden="true" />
                {:else}
                    <PlusIcon class="w-6 h-6" aria-hidden="true" />
                {/if}
            </button>
        </div>
    {/each}
</div>

<div
    bind:this={popoverElement}
    id="hotspot-popover"
    class:hidden={!openHotspot}
    class="absolute z-[1050] w-96 max-w-full bg-prevent-background-honeydew rounded-lg shadow-xl"
    role="region"
    aria-labelledby="hotspot-title"
    aria-describedby="hotspot-description">
    {#if openHotspot}
        <div class="px-5 py-6 editor-links">
            <div class="flex justify-between items-start mb-3">
                <h3 id="hotspot-title" class="text-2xl font-flama pr-2">
                    {openHotspot.title}
                </h3>
                <button
                    bind:this={closeButtonElement}
                    type="button"
                    on:click={closeHotspot}
                    class="flex-shrink-0 text-gray-600 hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                    aria-label="Stäng bildpunkt">
                    <CancelIcon class="w-5 h-5" aria-hidden="true" />
                </button>
            </div>

            <p
                id="hotspot-description"
                class="text-gray-600 text-base mb-4 leading-relaxed whitespace-pre-wrap">
                {openHotspot.text}
            </p>

            {#if openHotspot.link}
                <Button
                    href={openHotspot.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    theme="link"
                    size="none">
                    {openHotspot.linkText || 'Läs mer'}
                    <ExternalLinkIcon class="w-6 h-6" />
                </Button>
            {/if}

            {#if isProcess && (canGoPrevious || canGoNext)}
                <div class="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                    {#if canGoPrevious}
                        <Button
                            theme="primary-green-inverted"
                            type="button"
                            class="justify-center"
                            on:click={goToPrevious}>
                            <ChevronLeftIcon />
                            Tillbaka
                        </Button>
                    {:else}
                        <div></div>
                    {/if}

                    {#if canGoNext}
                        <Button
                            theme="primary"
                            type="button"
                            class="justify-center"
                            on:click={goToNext}>
                            Nästa
                            <ChevronRightCircleFilled />
                        </Button>
                    {/if}
                </div>
            {/if}
        </div>
    {/if}
</div>
